import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthV2 } from '../../context/auth-v2/AuthContext';
import type { UserRole } from '../../types/roles';
import { isBusinessUser, isCharityUser, isSuperAdmin } from '../../types/user-schema';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  redirectPath?: string;
}

/**
 * Improved Protected Route Component
 * 
 * Features:
 * - Role-based access control
 * - Redirects unauthenticated users to login
 * - Redirects unauthorized users to appropriate page
 * - Handles loading state
 */
const ProtectedRouteV2: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  redirectPath = '/login',
}) => {
  const { user, isLoading, isAuthenticated } = useAuthV2();
  const location = useLocation();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // If no specific roles are required, allow access
  if (requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has any of the required roles
  const hasRequiredRole = requiredRoles.some(role => {
    switch (role) {
      case 'business':
        return isBusinessUser(user);
      case 'charity':
        return isCharityUser(user);
      case 'super_admin':
        return isSuperAdmin(user);
      default:
        return user.role === role;
    }
  });

  // Redirect to appropriate page if user doesn't have required role
  if (!hasRequiredRole) {
    // Determine where to redirect based on user's role
    let redirectTo = '/unauthorized';
    
    if (isBusinessUser(user)) {
      redirectTo = '/business/dashboard';
    } else if (isCharityUser(user)) {
      redirectTo = '/charity/dashboard';
    }
    
    return <Navigate to={redirectTo} replace />;
  }

  // User is authenticated and has required role
  return <>{children}</>;
};

export default ProtectedRouteV2;